/**
 * ISELA THEME — Produktionsschicht (Phase A)
 *
 * Ersetzt fünf Experimentalskripte. Grundregeln dieser Datei:
 *
 * 1. Progressive Enhancement. Nichts hier ist Voraussetzung dafür, dass Inhalt
 *    sichtbar oder benutzbar ist. Fällt diese Datei komplett aus, funktioniert
 *    der Shop weiterhin – nur weniger komfortabel.
 * 2. Kein opacity/visibility-Management. Kein Element wird versteckt und auf
 *    einen Observer gewartet. Genau das hat im Altbestand ganze Seiten leer
 *    gerendert, sobald der Observer im Theme-Editor nicht feuerte.
 * 3. Kein window.location, keine Redirects.
 * 4. Kein customElements.define – keine Kollision mit Dawn.
 * 5. Jede Funktion ist gegen Mehrfachaufruf gesichert, weil der Theme-Editor
 *    Sections neu rendert und shopify:section:load erneut auslöst.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* --------------------------------------------------------------------
   * Farb-Swatches auf Produktkarten
   * Ein Klick tauscht nur das Kartenbild. Der Kartenlink bleibt funktionsfähig,
   * ohne JavaScript verhält sich die Karte wie eine normale Produktkarte.
   * ------------------------------------------------------------------ */
  function initCardSwatches() {
    if (document.body.dataset.iselaSwatches === 'on') return;
    document.body.dataset.iselaSwatches = 'on';

    document.addEventListener('click', function (event) {
      var swatch = event.target.closest('.isela-swatch');
      if (!swatch || swatch.classList.contains('isela-swatch--more')) return;

      var card = swatch.closest('.card-wrapper');
      var group = swatch.closest('.isela-swatches');
      if (!card || !group) return;

      event.preventDefault();

      group.querySelectorAll('.isela-swatch').forEach(function (other) {
        other.setAttribute('aria-pressed', other === swatch ? 'true' : 'false');
      });

      var src = swatch.getAttribute('data-isela-image');
      var img = card.querySelector('.card__media img');
      if (src && img) {
        var preload = new Image();
        preload.onload = function () {
          img.removeAttribute('srcset');
          img.src = src;
        };
        preload.src = src;
      }

      var url = swatch.getAttribute('data-isela-url');
      if (url) {
        card.querySelectorAll('a[href]').forEach(function (link) {
          if (link.closest('.isela-swatches')) return;
          link.href = url;
        });
      }
    });
  }

  /* --------------------------------------------------------------------
   * Sticky Kaufleiste (Mobile)
   * Klickt den echten Submit-Button des Produktformulars, statt selbst ans
   * Cart-API zu gehen. So bleiben Variantenlogik, Mengen, Fehlermeldungen und
   * der Cart-Drawer unverändert Shopify-Standard.
   * ------------------------------------------------------------------ */
  function initBuyBar() {
    var bar = document.querySelector('[data-isela-buybar]');
    if (!bar || bar.dataset.iselaReady === 'on') return;

    var anchor = document.querySelector('[data-isela-buy-anchor]');
    if (!anchor) return;

    bar.dataset.iselaReady = 'on';
    document.body.classList.add('isela-has-buybar');

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            bar.classList.toggle('is-visible', scrolledPast);
          });
        },
        { threshold: 0 }
      ).observe(anchor);
    } else {
      // Ohne Observer bleibt die Leiste dauerhaft sichtbar – lieber immer da
      // als nie da.
      bar.classList.add('is-visible');
    }

    var action = bar.querySelector('[data-isela-buybar-submit]');
    if (!action) return;

    action.addEventListener('click', function (event) {
      event.preventDefault();
      var submit = anchor.querySelector('[type="submit"]:not([disabled])');
      if (submit) {
        submit.click();
        return;
      }
      anchor.scrollIntoView({
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
        block: 'center'
      });
    });

    // Preis und Beschriftung beim Variantenwechsel mitziehen. Dawn liefert die
    // frisch gerenderte Section im Payload mit.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (payload) {
        if (!payload || !payload.data) return;
        var data = payload.data;
        var priceEl = bar.querySelector('[data-isela-buybar-price]');
        if (priceEl && data.html && data.sectionId) {
          var fresh = data.html.getElementById('price-' + data.sectionId);
          if (fresh) { var onSale = fresh.querySelector('.price--on-sale'); var pv = fresh.querySelector(onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular'); priceEl.textContent = (pv ? pv.textContent : fresh.textContent).trim().replace(/\s+/g, ' ').replace(/\s*EUR$/, ''); }
        }
        var available = Boolean(data.variant && data.variant.available);
        action.textContent = available
          ? action.getAttribute('data-label-add')
          : action.getAttribute('data-label-soldout');
      });
    }
  }

  /* --------------------------------------------------------------------
   * Variantenwahl: nie eine Kombination anfragen, die es nicht gibt
   * Beispiel: "Matcha Green" existiert nur in L/XL. War S gewählt, fragte Dawn
   * "Matcha Green / S" an, bekam keine Variante und brach das Galerie-Update
   * ab – sichtbar blieb das Bild der vorigen Farbe. Dieser Listener läuft in
   * der Capture-Phase, also bevor Dawn die gewählten Werte liest, und setzt die
   * nachfolgenden Optionen auf die erste passende (bevorzugt lieferbare)
   * Variante. Existiert die Kombination, passiert nichts.
   * ------------------------------------------------------------------ */
  function initVariantFallback() {
    if (document.body.dataset.iselaVariantFallback === 'on') return;
    document.body.dataset.iselaVariantFallback = 'on';

    document.addEventListener(
      'change',
      function (event) {
        var input = event.target;
        if (!input || input.type !== 'radio') return;
        var selects = input.closest('variant-selects');
        var data = selects && selects.querySelector('script[data-isela-variants]');
        if (!data) return;

        var variants;
        try {
          variants = JSON.parse(data.textContent);
        } catch (e) {
          return;
        }

        var fieldsets = Array.prototype.slice.call(selects.querySelectorAll('fieldset'));
        var changed = fieldsets.indexOf(input.closest('fieldset'));
        if (changed < 0) return;

        var chosen = fieldsets.map(function (fieldset) {
          var checked = fieldset.querySelector('input:checked');
          return checked ? checked.value : null;
        });
        var same = function (options, upTo) {
          for (var i = 0; i <= upTo; i++) if (options[i] !== chosen[i]) return false;
          return true;
        };

        if (variants.some(function (v) { return same(v[0], chosen.length - 1); })) return;

        var candidates = variants.filter(function (v) { return same(v[0], changed); });
        if (!candidates.length) return;
        var best = candidates.filter(function (v) { return v[1]; })[0] || candidates[0];

        for (var i = changed + 1; i < fieldsets.length; i++) {
          var target = Array.prototype.filter.call(fieldsets[i].querySelectorAll('input[type="radio"]'), function (radio) {
            return radio.value === best[0][i];
          })[0];
          if (target) target.checked = true;
        }
      },
      true
    );
  }

  /* --------------------------------------------------------------------
   * Lange Kollektionsbeschreibung einklappen
   * Die Klasse, die klemmt, setzt erst dieses Skript. Ohne JavaScript bleibt
   * der vollständige Text stehen – der Inhalt geht nie verloren.
   * ------------------------------------------------------------------ */
  function initClamp() {
    var narrow = window.matchMedia('(max-width: 749px)');

    document.querySelectorAll('[data-isela-clamp]').forEach(function (block) {
      if (block.dataset.iselaClampReady === 'on') return;
      block.dataset.iselaClampReady = 'on';

      var toggle = null;

      var apply = function () {
        block.classList.remove('is-clamped');

        if (!narrow.matches) {
          if (toggle) toggle.hidden = true;
          return;
        }

        /* Reihenfolge ist entscheidend: erst klemmen, DANN messen.
         * Vorher wurde ungeklemmt gemessen – da sind scrollHeight und
         * clientHeight zwangsläufig gleich, der Block galt nie als zu lang
         * und wurde nie eingeklappt. Live nachgewiesen: das erste Produkt der
         * Collection stand dadurch bei y=967px, hinter der Falz. */
        block.classList.add('is-clamped');

        var overflows = block.scrollHeight > block.clientHeight + 8;
        if (!overflows) {
          block.classList.remove('is-clamped');
          if (toggle) toggle.hidden = true;
          return;
        }

        if (!toggle) {
          toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'isela-more';
          toggle.setAttribute('aria-expanded', 'false');
          toggle.textContent = 'Mehr lesen';
          toggle.addEventListener('click', function () {
            var open = block.classList.toggle('is-clamped') === false;
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
            toggle.textContent = open ? 'Weniger anzeigen' : 'Mehr lesen';
          });
          block.insertAdjacentElement('afterend', toggle);
        }
        toggle.hidden = false;
      };

      apply();
      // Gleiche Absicherung wie bei Shop the Look: aeltere Safari-Staende kennen
      // addEventListener am MediaQueryList noch nicht.
      if (narrow.addEventListener) narrow.addEventListener('change', apply);
      else if (narrow.addListener) narrow.addListener(apply);
    });
  }

  /* --------------------------------------------------------------------
   * Footer-Menüs auf Mobile einklappen
   * Ohne JavaScript bleibt alles aufgeklappt – der heutige Zustand.
   * ------------------------------------------------------------------ */
  function initFooterAccordion() {
    var narrow = window.matchMedia('(max-width: 749px)');

    document.querySelectorAll('.footer-block--menu').forEach(function (block, index) {
      if (block.dataset.iselaAccordion === 'on') return;

      var heading = block.querySelector('.footer-block__heading');
      var content = block.querySelector('.footer-block__details-content');
      if (!heading || !content) return;

      block.dataset.iselaAccordion = 'on';
      content.id = content.id || 'isela-footer-panel-' + index;

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'isela-footer-toggle';
      button.setAttribute('aria-controls', content.id);
      button.innerHTML =
        '<span>' + heading.innerHTML + '</span><span class="isela-footer-toggle__icon" aria-hidden="true"></span>';
      heading.innerHTML = '';
      heading.appendChild(button);

      var setOpen = function (open) {
        block.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
      };

      var apply = function () {
        if (narrow.matches) {
          block.classList.add('isela-footer-collapsible');
          setOpen(false);
        } else {
          block.classList.remove('isela-footer-collapsible');
          block.classList.add('is-open');
          button.removeAttribute('aria-expanded');
        }
      };

      button.addEventListener('click', function () {
        if (!narrow.matches) return;
        setOpen(button.getAttribute('aria-expanded') !== 'true');
      });

      apply();
      // Gleiche Absicherung: aeltere Safari-Staende kennen addEventListener
      // am MediaQueryList noch nicht.
      if (narrow.addEventListener) narrow.addEventListener('change', apply);
      else if (narrow.addListener) narrow.addListener(apply);
    });
  }

  /* --------------------------------------------------------------------
   * MOTION-SYSTEM
   *
   * Sicherheitsarchitektur, aus dem Ausfall der Vorgängerversion gelernt:
   * Die CSS-Regel, die etwas verbirgt, greift ausschliesslich unter
   * html.isela-motion. Diese Klasse setzt NUR dieses Skript – und erst,
   * nachdem der Observer nachweislich steht. Fällt JavaScript aus, läuft der
   * Theme-Editor, oder ist reduzierte Bewegung gewünscht, wird sie nie
   * gesetzt und alles bleibt sichtbar.
   *
   * Zusätzlich zwei Netze: die erste Section wird nie verborgen, und ein
   * Timeout deckt auf, was der Observer verpasst hat.
   * ------------------------------------------------------------------ */
  function initMotion() {
    var root = document.documentElement;

    if (window.Shopify && window.Shopify.designMode) return;
    if (reduceMotion.matches) return;
    if (!('IntersectionObserver' in window)) return;

    var targets = document.querySelectorAll('[data-isela-reveal]');
    if (!targets.length) return;

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el, i) {
      // Staffelung innerhalb einer Gruppe, gedeckelt bei sechs Stufen –
      // darüber hinaus wirkt das letzte Element abgehängt statt elegant.
      var group = el.closest('[data-isela-stagger]');
      if (group) {
        var idx = [].indexOf.call(group.querySelectorAll('[data-isela-reveal]'), el);
        el.style.setProperty('--isela-delay', Math.min(idx, 5) * 70 + 'ms');
      }
      observer.observe(el);
    });

    // Erst jetzt darf überhaupt etwas verborgen werden.
    root.classList.add('isela-motion');

    // Was sofort im Bild ist, wird ohne Wartezeit aufgedeckt.
    targets.forEach(function (el) {
      if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-in');
    });

    // Netz: alles, was nach 2s im Sichtbereich noch verborgen wäre, aufdecken.
    window.setTimeout(function () {
      document.querySelectorAll('[data-isela-reveal]:not(.is-in)').forEach(function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight * 1.2) el.classList.add('is-in');
      });
    }, 2000);
  }

  /* --------------------------------------------------------------------
   * Header-Scroll-Zustand
   * Nur eine Klasse; die Gestaltung passiert vollständig in CSS.
   * ------------------------------------------------------------------ */
  function initHeaderState() {
    var header = document.querySelector('.section-header');
    if (!header || header.dataset.iselaScroll === 'on') return;
    header.dataset.iselaScroll = 'on';

    var ticking = false;
    var update = function () {
      document.documentElement.classList.toggle('isela-scrolled', window.scrollY > 40);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  }

  /* --------------------------------------------------------------------
   * Shop the Look — Hotspots
   *
   * Ohne JavaScript liegt unter dem Bild eine ganz normale, vollständig
   * bedienbare Produktliste. Erst dieses Skript macht daraus ein Popover
   * (Desktop) bzw. ein Bottom Sheet (Mobile) und aktiviert die Punkte.
   * ------------------------------------------------------------------ */
  function initShopTheLook() {
    document.querySelectorAll('[data-isela-stl]').forEach(function (root) {
      if (root.dataset.iselaStlReady === 'on') return;
      root.dataset.iselaStlReady = 'on';

      var pins = root.querySelectorAll('[data-isela-stl-pin]');
      var cards = root.querySelectorAll('[data-isela-stl-card]');
      var scrim = root.querySelector('[data-isela-stl-scrim]');
      if (!pins.length || !cards.length) return;

      // Erst jetzt darf das CSS die Liste als Overlay behandeln.
      root.classList.add('isela-stl--interactive');
      if (scrim) scrim.hidden = false;
      root.querySelectorAll('[data-isela-stl-close]').forEach(function (b) { b.hidden = false; });

      var narrow = window.matchMedia('(max-width: 749px)');
      var openIndex = null;

      /* Geschlossene Karten bekommen hidden statt nur eine Klasse.
       * Eine Karte, die per CSS unsichtbar ist, bleibt sonst mit Tab
       * erreichbar und wird vorgelesen – der Nutzer landete im Nichts.
       * hidden erledigt Sichtbarkeit, Fokus und Screenreader in einem. */
      var setCards = function (index) {
        cards.forEach(function (c) {
          var on = c.getAttribute('data-isela-stl-card') === index;
          c.classList.toggle('is-open', on);
          c.hidden = !on;
        });
      };

      // Alle Karten starten geschlossen, sobald das Skript uebernimmt.
      setCards(null);

      var close = function (returnFocus) {
        var last = openIndex;
        openIndex = null;
        pins.forEach(function (p) { p.setAttribute('aria-expanded', 'false'); p.classList.remove('is-active'); });
        setCards(null);
        root.classList.remove('is-sheet-open');

        // Der Fokus muss zurueck an den Punkt, von dem er kam.
        if (returnFocus && last !== null) {
          var origin = root.querySelector('[data-isela-stl-pin="' + last + '"]');
          if (origin) origin.focus();
        }
      };

      var open = function (index) {
        if (openIndex === index) { close(true); return; }
        openIndex = index;
        pins.forEach(function (p) {
          var on = p.getAttribute('data-isela-stl-pin') === index;
          p.setAttribute('aria-expanded', on ? 'true' : 'false');
          p.classList.toggle('is-active', on);
        });
        setCards(index);
        root.classList.toggle('is-sheet-open', narrow.matches);

        var card = root.querySelector('[data-isela-stl-card="' + index + '"]');
        if (card) card.focus();
      };

      pins.forEach(function (pin) {
        pin.addEventListener('click', function () { open(pin.getAttribute('data-isela-stl-pin')); });
      });
      root.querySelectorAll('[data-isela-stl-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { close(true); });
      });
      // Klick daneben schliesst, gibt den Fokus aber nicht zurueck: der Nutzer
      // zeigt damit gerade woanders hin.
      if (scrim) scrim.addEventListener('click', function () { close(false); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && openIndex !== null) close(true);
      });

      /* Beim Wechsel zwischen Sheet und Popover schliessen: sonst haengt ein
       * offenes Bottom Sheet nach dem Drehen als Popover im Bild. */
      var onBreak = function () { close(false); };
      if (narrow.addEventListener) narrow.addEventListener('change', onBreak);
      else if (narrow.addListener) narrow.addListener(onBreak);
    });
  }

  /* --------------------------------------------------------------------
   * Videos — Hero, Story, Reels und PDP
   *
   * Ersetzt initReels. Es gibt jetzt EINE Stelle, an der entschieden wird,
   * ob ein Video laden und laufen darf. Vorher galten die Regeln nur für
   * Reels; ein Hero-Video hätte sie umgangen.
   *
   * Vier Gründe, ein Video NICHT zu starten:
   *   1. Theme-Editor. Dort ist Bewegung im Weg, nicht hilfreich.
   *   2. prefers-reduced-motion.
   *   3. Datensparmodus des Browsers.
   *   4. Kein IntersectionObserver — dann bleibt das Posterbild stehen.
   * In allen vier Fällen bleibt das Poster sichtbar. Es geht nie Inhalt
   * verloren, weil das Poster ein normales <img> im Markup ist.
   *
   * Nie mehr als zwei Clips gleichzeitig: vier parallele Dekodierungen
   * lassen ältere Handys sichtbar ruckeln, und jeder laufende Clip lädt.
   *
   * T-009: Kein Clip startet vor dem load-Ereignis der Seite. Gemessen lief
   * das mobile Hero-Video ab ~1,8 s parallel zum LCP-Bild und teilte sich mit
   * ihm die Bandbreite (LCP 3,1 s bei gedrosseltem Mobilnetz). Das Poster ist
   * ohnehin sichtbar; der Clip blendet danach wie bisher weich ein.
   * ------------------------------------------------------------------ */
  var iselaPlaying = [];
  var iselaLoaded = document.readyState === 'complete';
  var iselaPending = [];

  if (!iselaLoaded) {
    window.addEventListener(
      'load',
      function () {
        // Ein Takt Abstand, damit load-Handler von Shopify zuerst laufen.
        window.setTimeout(function () {
          iselaLoaded = true;
          iselaPending.splice(0).forEach(function (fn) { fn(); });
        }, 0);
      },
      { once: true }
    );
  }

  function iselaStop(video) {
    var i = iselaPlaying.indexOf(video);
    if (i > -1) iselaPlaying.splice(i, 1);
    if (!video.paused) video.pause();
    video.closest('[data-isela-video]').classList.remove('is-playing');
  }

  function iselaStart(video) {
    if (iselaPlaying.indexOf(video) > -1) return;
    // Ältester weicht, damit höchstens zwei Clips gleichzeitig laufen.
    while (iselaPlaying.length >= 2) iselaStop(iselaPlaying[0]);

    iselaPlaying.push(video);
    video.muted = true;
    var wrap = video.closest('[data-isela-video]');
    var p = video.play();
    if (p && typeof p.catch === 'function') {
      // Ein abgelehntes play() ist kein Fehler: manche Browser verweigern
      // es im Hintergrundtab. Das Poster bleibt, mehr passiert nicht.
      p.then(function () { wrap.classList.add('is-playing'); }).catch(function () { iselaStop(video); });
    } else {
      wrap.classList.add('is-playing');
    }
  }

  function initVideo() {
    var wraps = document.querySelectorAll('[data-isela-video]');
    if (!wraps.length) return;

    var designMode = window.Shopify && window.Shopify.designMode;
    var saveData = navigator.connection && navigator.connection.saveData;
    var still = designMode || reduceMotion.matches || saveData || !('IntersectionObserver' in window);

    wraps.forEach(function (wrap) {
      if (wrap.dataset.iselaVideoReady === 'on') return;
      wrap.dataset.iselaVideoReady = 'on';

      var video = wrap.querySelector('video');
      if (!video) return;

      video.muted = true;
      video.setAttribute('playsinline', '');
      // Falls Shopifys video_tag autoplay gesetzt hat: hier wieder weg.
      video.removeAttribute('autoplay');

      /* Reichweite: der Hero kann auf "nur mobil" stehen, weil die Clips
       * hochkant sind. Ohne diese Prüfung würde der Desktop den Clip zwar
       * nicht zeigen, ihn aber laden – 2 MB für nichts.
       *
       * Die Prüfung lief zuerst nur EINMAL beim Laden. Wurde das Fenster
       * danach breiter, lief der hochkante Clip als Vollflächen-Hero weiter
       * und wurde auf 16:9 beschnitten – im Browser nachgewiesen. Deshalb
       * hängt sie jetzt an der Medienabfrage und greift bei jedem Wechsel. */
      var scope = wrap.getAttribute('data-isela-video-scope');
      var narrow = window.matchMedia('(max-width: 749px)');

      var strip = function () {
        var src = video.querySelector('source');
        if (src) {
          wrap.dataset.iselaVideoSrc = src.getAttribute('src');
          src.remove();
        }
        video.removeAttribute('src');
        video.setAttribute('preload', 'none');
        iselaStop(video);
        video.load();
      };

      var restore = function () {
        if (video.querySelector('source') || !wrap.dataset.iselaVideoSrc) return;
        var el = document.createElement('source');
        el.setAttribute('src', wrap.dataset.iselaVideoSrc);
        el.setAttribute('type', 'video/mp4');
        video.appendChild(el);
        video.load();
      };

      var applyScope = function () {
        if (scope !== 'mobile') return;
        if (narrow.matches) restore();
        else strip();
      };

      if (still || wrap.hasAttribute('data-isela-video-manual')) {
        video.setAttribute('preload', 'none');
        return;
      }

      applyScope();
      if (narrow.addEventListener) narrow.addEventListener('change', applyScope);
      else if (narrow.addListener) narrow.addListener(applyScope);

      /* Kein frueher Ausstieg: der Beobachter wird IMMER angehaengt. Sonst
       * bliebe ein Hero, der breit geladen und dann schmal gezogen wird, fuer
       * immer stumm stehen. Ob wirklich abgespielt wird, entscheidet die
       * Reichweitenpruefung unten bei jedem Sichtwechsel neu. */
      var inScope = function () {
        return scope !== 'mobile' || narrow.matches;
      };

      // Letzter bekannter Sichtzustand. Vor dem load-Ereignis wird nur
      // gemerkt; danach entscheidet derselbe Zustand, ob der Clip startet.
      var visible = false;
      var queued = false;

      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            visible = entry.isIntersecting;
            if (!iselaLoaded) {
              if (!queued) {
                queued = true;
                iselaPending.push(function () {
                  if (visible && inScope()) iselaStart(video);
                });
              }
              return;
            }
            if (visible && inScope()) iselaStart(video);
            else iselaStop(video);
          });
        },
        { threshold: 0.35 }
      ).observe(wrap);


      /* Kein Ton-Schalter. Die Clips tragen zwar noch eine Tonspur, aber sie
       * ist auf keinem Weg erreichbar: muted bleibt gesetzt, es gibt keine
       * controls und keine Schaltflaeche. Ton laeuft nie - weder automatisch
       * noch auf Klick. */
    });
  }
  function init() {
    initCardSwatches();
    initBuyBar();
    initVariantFallback();
    initClamp();
    initFooterAccordion();
    initMotion();
    initHeaderState();
    initShopTheLook();
    initVideo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Der Theme-Editor rendert Sections neu; alle Funktionen sind idempotent.
  document.addEventListener('shopify:section:load', init);
})();
